import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Globe, Plus, Trash2, Shield, ShieldOff, Edit2, CheckCircle, XCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function SubdomainManagement() {
  const [subdomains, setSubdomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    target_port: 3000,
    target_path: '/',
    record_type: 'A',
    ip_address: '',
    cname_target: '',
    ssl_enabled: false
  });

  useEffect(() => {
    fetchSubdomains();
  }, []);

  const fetchSubdomains = async () => {
    try {
      const response = await axios.get(`${API}/subdomains`, getAuthHeaders());
      setSubdomains(response.data);
    } catch (error) {
      console.error('Error fetching subdomains:', error);
      toast.error('Ошибка загрузки поддоменов');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      target_port: 3000,
      target_path: '/',
      record_type: 'A',
      ip_address: '',
      cname_target: '',
      ssl_enabled: false
    });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Введите имя поддомена');
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API}/subdomains/${editingId}`, formData, getAuthHeaders());
        toast.success('Поддомен обновлён');
      } else {
        await axios.post(`${API}/subdomains`, formData, getAuthHeaders());
        toast.success('Поддомен создан успешно');
      }
      
      resetForm();
      fetchSubdomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения поддомена');
    }
  };

  const handleEdit = (subdomain) => {
    setFormData({
      name: subdomain.name,
      target_port: subdomain.target_port || 3000,
      target_path: subdomain.target_path || '/',
      record_type: subdomain.record_type || 'A',
      ip_address: subdomain.ip_address || '',
      cname_target: subdomain.cname_target || '',
      ssl_enabled: subdomain.ssl_enabled || false
    });
    setEditingId(subdomain.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Удалить поддомен ${name}.busas.lt?`)) return;

    try {
      await axios.delete(`${API}/subdomains/${id}`, getAuthHeaders());
      toast.success('Поддомен удалён');
      fetchSubdomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления поддомена');
    }
  };

  const handleToggleSSL = async (id, currentStatus) => {
    try {
      await axios.post(`${API}/subdomains/${id}/toggle-ssl`, {}, getAuthHeaders());
      toast.success(currentStatus ? 'SSL отключен' : 'SSL включен');
      fetchSubdomains();
    } catch (error) {
      toast.error('Ошибка переключения SSL');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'inactive':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <XCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-700 border-green-300',
      inactive: 'bg-red-100 text-red-700 border-red-300',
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      error: 'bg-red-100 text-red-700 border-red-300'
    };
    const labels = {
      active: 'Активен',
      inactive: 'Неактивен',
      pending: 'В процессе',
      error: 'Ошибка'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${badges[status] || badges.error}`}>
        {getStatusIcon(status)}
        <span className="ml-1">{labels[status] || 'Неизвестно'}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="text-lg text-gray-600">Загрузка...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-effect shadow-2xl border-2 border-cyan-200 hover:border-cyan-400 transition-all">
      <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 border-b-2 border-cyan-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center text-2xl">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                <Globe className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              Управление поддоменами
            </CardTitle>
            <CardDescription className="text-base font-medium text-gray-600">
              Создание и настройка поддоменов для busas.lt
            </CardDescription>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowAddForm(!showAddForm);
            }}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold shadow-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            {showAddForm ? 'Отмена' : 'Добавить поддомен'}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-6 space-y-6">
        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="border-2 border-cyan-200 rounded-xl p-6 bg-gradient-to-br from-cyan-50 to-blue-50">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingId ? 'Редактировать поддомен' : 'Новый поддомен'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Имя поддомена</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="api"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase() })}
                      className="border-2 border-cyan-200 focus:border-cyan-500"
                    />
                    <span className="text-gray-600 font-medium whitespace-nowrap">.busas.lt</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Порт</Label>
                  <Input
                    type="number"
                    placeholder="3000"
                    value={formData.target_port}
                    onChange={(e) => setFormData({ ...formData, target_port: parseInt(e.target.value) || 3000 })}
                    className="border-2 border-cyan-200 focus:border-cyan-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Путь</Label>
                  <Input
                    placeholder="/"
                    value={formData.target_path}
                    onChange={(e) => setFormData({ ...formData, target_path: e.target.value })}
                    className="border-2 border-cyan-200 focus:border-cyan-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Тип записи DNS</Label>
                  <select
                    value={formData.record_type}
                    onChange={(e) => setFormData({ ...formData, record_type: e.target.value })}
                    className="w-full border-2 border-cyan-200 rounded-md p-2 focus:border-cyan-500 focus:outline-none"
                  >
                    <option value="A">A (IP адрес)</option>
                    <option value="CNAME">CNAME (алиас)</option>
                  </select>
                </div>

                {formData.record_type === 'A' && (
                  <div className="space-y-2">
                    <Label>IP адрес</Label>
                    <Input
                      placeholder="127.0.0.1"
                      value={formData.ip_address}
                      onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                      className="border-2 border-cyan-200 focus:border-cyan-500"
                    />
                  </div>
                )}

                {formData.record_type === 'CNAME' && (
                  <div className="space-y-2">
                    <Label>CNAME цель</Label>
                    <Input
                      placeholder="example.com"
                      value={formData.cname_target}
                      onChange={(e) => setFormData({ ...formData, cname_target: e.target.value })}
                      className="border-2 border-cyan-200 focus:border-cyan-500"
                    />
                  </div>
                )}

                <div className="flex items-center gap-3 border-2 border-cyan-200 rounded-md px-4 bg-white">
                  <input
                    type="checkbox"
                    id="ssl-checkbox"
                    checked={formData.ssl_enabled}
                    onChange={(e) => setFormData({ ...formData, ssl_enabled: e.target.checked })}
                    className="w-5 h-5 accent-cyan-500"
                  />
                  <label htmlFor="ssl-checkbox" className="text-sm font-semibold cursor-pointer text-gray-700">
                    Включить SSL (HTTPS)
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold">
                  {editingId ? 'Обновить' : 'Создать'}
                </Button>
                <Button type="button" onClick={resetForm} variant="outline">
                  Отмена
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Subdomains Table */}
        <div>
          <h3 className="font-bold text-lg text-gray-900 mb-4">
            Список поддоменов ({subdomains.length})
          </h3>
          
          {subdomains.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-cyan-200 rounded-xl bg-cyan-50">
              <Globe className="w-16 h-16 text-cyan-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Поддомены не найдены</p>
              <p className="text-gray-500 text-sm mt-2">Создайте первый поддомен, нажав кнопку выше</p>
            </div>
          ) : (
            <div className="border-2 border-cyan-200 rounded-xl overflow-hidden shadow-lg">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-cyan-100 to-blue-100">
                      <TableHead className="font-bold text-gray-900">Поддомен</TableHead>
                      <TableHead className="font-bold text-gray-900">Порт</TableHead>
                      <TableHead className="font-bold text-gray-900">Путь</TableHead>
                      <TableHead className="font-bold text-gray-900">SSL</TableHead>
                      <TableHead className="font-bold text-gray-900">Статус</TableHead>
                      <TableHead className="font-bold text-gray-900 text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subdomains.map((subdomain) => (
                      <TableRow key={subdomain.id} className="hover:bg-cyan-50 transition-colors">
                        <TableCell className="font-semibold text-cyan-700">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4" />
                            {subdomain.full_domain}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-700">{subdomain.target_port}</TableCell>
                        <TableCell className="text-gray-700 font-mono text-sm">{subdomain.target_path}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleSSL(subdomain.id, subdomain.ssl_enabled)}
                            className={subdomain.ssl_enabled ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-600'}
                          >
                            {subdomain.ssl_enabled ? (
                              <Shield className="w-5 h-5" />
                            ) : (
                              <ShieldOff className="w-5 h-5" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>{getStatusBadge(subdomain.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(subdomain)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(subdomain.id, subdomain.name)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xl">ℹ️</span>
            </div>
            <div className="text-sm text-gray-700 space-y-1">
              <p className="font-bold text-gray-900">Как это работает?</p>
              <p>• <strong>Поддомен</strong> - создаёт новый адрес вида: название.busas.lt</p>
              <p>• <strong>Порт</strong> - на какой порт сервера перенаправлять запросы</p>
              <p>• <strong>SSL</strong> - автоматически создаст HTTPS сертификат</p>
              <p>• Изменения DNS могут занять до 5-10 минут</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
